import React from 'react';
import {Row, Col, Form} from 'react-bootstrap';
import * as yup from 'yup';
import Button from '@/components/ui/Button';
import {BaseSettingsForm, SelectField, RichTextEditorField} from '@/components/forms';
import {useStoreActions, useStoreState} from 'easy-peasy';
import useData from '@/utils/useData';
import {useAlert} from '@/components/alerts';
import i18n from 'i18n';

interface EvaluationTypeChoice {
    value: string;
    label: string;
}

interface Evaluation {
    id: number;
    evaluationIdentifier: string;
    evaluationLabel: string;
    evaluationType: string;
    description: string | null;
    active: boolean;
    priority: boolean;
    base: number | null;
    window: number | null;
}

interface FormValues {
    id: number | string;
    evaluationIdentifier: string;
    evaluationLabel: string;
    evaluationType: string;
    description: string | null;
    active: boolean;
    priority: boolean;
    base: number | null;
    window: number | null;
}

interface EvaluationsSettingsFormProps {
    evaluationId?: number;
    onSave?: () => void;
}

interface StoreState {
    evaluations: {
        items: Evaluation[];
    };
}

interface StoreActions {
    evaluations: {
        addOrEdit: (evaluation: Evaluation) => void;
    };
}

const EvaluationSettingsFormSchema = yup.object({
    evaluationIdentifier: yup.string()
        .max(64, i18n.t('jt_eval_id_max.text'))
        .required(i18n.t('jt_eval_id_required.text')),
    evaluationLabel: yup.string()
        .max(128, i18n.t('jt_eval_label_max.text')),
    evaluationType: yup.string()
        .required(i18n.t('jt_eval_type_required.text')),
    description: yup.string()
        .max(2000, i18n.t('jt_eval_desc_max.text'))
        .nullable(),
    active: yup.boolean()
        .required(i18n.t('jt_eval_active_required.text')),
});

const EvaluationsSettingsForm: React.FC<EvaluationsSettingsFormProps> = React.memo((
    {
        evaluationId = 0,
        onSave = () => { },
    },
) => {
    const {showSuccess} = useAlert();
    const evaluations = useStoreState((state: StoreState) => state.evaluations.items);
    const setEvaluation = useStoreActions((actions: StoreActions) => actions.evaluations.addOrEdit);
    const setReloadEvaluations = useStoreActions((actions: any) => actions.evaluations.setReload);

    const [evaluationTypeChoices] = useData<EvaluationTypeChoice[]>(window.URLS.evaluationTypeChoicesResource, {
        storeResolver: 'viewTypeFieldChoices',
        actionResolver: 'loadEvaluationTypeChoices',
        dataResolver: 'evaluationTypeChoices',
    });

    const initialValues: FormValues = {
        id: '',
        evaluationIdentifier: '',
        evaluationLabel: '',
        evaluationType: '',
        description: '',
        active: true,
        priority: false,
        base: null,
        window: null,
    };

    const checkIdentifierExistsLocally = (identifier: string): boolean => {
        return evaluations.some(evaluation => evaluation.evaluationIdentifier === identifier && evaluation.id !== evaluationId);
    };

    const customErrorHandling = (error: any, formik: any) => {
        // Check for local identifier conflicts first
        if (checkIdentifierExistsLocally(formik.values.evaluationIdentifier)) {
            formik.setFieldError('evaluationIdentifier', i18n.t('jt_eval_id_exists.text'));
            return true;
        }
        return false;
    };

    const customSuccessHandling = (response: any, actionName: string) => {
        setEvaluation(response.data);
        showSuccess(i18n.t('jt_eval_success_action.text', {actionName}));
        setReloadEvaluations(true);
        onSave?.();
    };

    return (
        <BaseSettingsForm
            entityId={evaluationId || undefined}
            resourceUrl={window.URLS.evaluationsResource}
            storeActionPath="evaluations.addOrEdit"
            validationSchema={EvaluationSettingsFormSchema}
            initialValues={initialValues}
            successMessageKey="jt_eval_success_action.text"
            onSave={onSave}
            customErrorHandling={customErrorHandling}
            customSuccessHandling={customSuccessHandling}
        >
            {(formik, loading) => (
                <>
                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="evaluationIdentifier" >
                                <Form.Label>{i18n.t('jt_identifier.text')}</Form.Label>
                                <Form.Control
                                    name="evaluationIdentifier"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.evaluationIdentifier}
                                    isInvalid={formik.touched.evaluationIdentifier && !!formik.errors.evaluationIdentifier}
                                    autoFocus
                                    disabled={!!evaluationId}
                                    data-cy="eval-settings-identifier-input"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.evaluationIdentifier}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="evaluationLabel" >
                                <Form.Label>{i18n.t('jt_label.text')}</Form.Label>
                                <Form.Control
                                    name="evaluationLabel"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.evaluationLabel}
                                    isInvalid={formik.touched.evaluationLabel && !!formik.errors.evaluationLabel}
                                    data-cy="eval-settings-label-input"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.evaluationLabel}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="description" >
                                <Form.Label>{i18n.t('jt_description.text')}</Form.Label>
                                <RichTextEditorField
                                    name="description"
                                    value={formik.values.description || ''}
                                    onChange={(value) => formik.setFieldValue('description', value)}
                                    isRequired={false}
                                    error={formik.errors.description}
                                    touched={formik.touched.description}
                                />
                            </Form.Group>
                        </Col>
                    </Row >

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" >
                                <Form.Check
                                    name="active"
                                    label={i18n.t('jt_active_label.text')}
                                    type="switch"
                                    onChange={formik.handleChange}
                                    checked={formik.values.active}
                                    isInvalid={!!formik.errors.active}
                                    feedback={formik.errors.active}
                                    id="notificationActive"
                                />
                            </Form.Group>
                        </Col>
                    </Row >
                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" >
                                <Form.Check
                                    name="priority"
                                    label={i18n.t('jt_priority_label.text')}
                                    type="switch"
                                    onChange={formik.handleChange}
                                    checked={formik.values.priority}
                                    isInvalid={!!formik.errors.priority}
                                    feedback={formik.errors.priority}
                                    id="notificationPriority"
                                    data-cy="eval-priority-switch"
                                />
                            </Form.Group>
                        </Col>
                    </Row >

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="evaluationType" >
                                <Form.Label>{i18n.t('jt_type.text')}</Form.Label>
                                <SelectField
                                    name="evaluationType"
                                    value={evaluationTypeChoices?.find((x) => x.value === formik.values.evaluationType) || null}
                                    onChange={(value) => formik.setFieldValue('evaluationType', value?.value || '')}
                                    options={evaluationTypeChoices || []}
                                    isRequired={true}
                                    isDisabled={!!evaluationId}
                                    error={formik.errors.evaluationType}
                                    touched={formik.touched.evaluationType}
                                />
                            </Form.Group>
                        </Col>
                    </Row >

                    {
                        formik.values.evaluationType === 'numerical_descriptor' || formik.values.evaluationType === 'feedback_numerical_descriptor' ? (
                            <>
                                <Row>
                                    <Col xs={12}>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>{i18n.t('jt_histogram_step.text')}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="base"
                                                value={formik.values.base || ''}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12}>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>{i18n.t('jt_window.text')}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="window"
                                                value={formik.values.window || ''}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row >
                            </>
                        ) : null
                    }

                    <Row>
                        <Form.Group className="ml-auto mb-3" controlId="story-save">
                            <Button
                                variant="info"
                                fullWidth
                                type="submit"
                                data-cy="eval-settings-save-btn"
                            >
                                {i18n.t('jt_save.text')}
                            </Button>
                        </Form.Group>
                    </Row>
                </>
            )}
        </BaseSettingsForm >
    );
});


export default EvaluationsSettingsForm; 